<template>
  <div class="raf" :class="'is-' + phase">
    <div class="raf__bg" aria-hidden="true">
      <div class="raf__glow raf__glow--1" />
      <div class="raf__glow raf__glow--2" />
      <div class="raf__halo" :class="{ 'is-hot': phase === 'pending' }" />
    </div>

    <div class="raf__head">
      <div class="raf__banner">{{ confirmed ? '中奖揭晓' : label }}</div>
      <div v-if="prizeName && !confirmed" class="raf__prize-name">{{ prizeName }}</div>
    </div>

    <!-- 待机慢转；抽奖中加速 + 中心切换「抽奖中」，直到控台确认 -->
    <div v-if="phase !== 'reveal'" class="raf__ring" :class="{ 'is-fast': phase === 'pending' }">
      <div class="raf__wheel">
        <div v-for="item in slots" :key="item.key" class="raf__slot" :style="item.style">
          <img v-if="item.avatar" class="raf__avatar" :src="item.avatar" alt="" />
          <span v-else class="raf__avatar is-text">{{ item.initial }}</span>
        </div>
      </div>
      <div class="raf__core" :class="{ 'is-hot': phase === 'pending' }">
        <strong>{{ phase === 'pending' ? '抽奖中' : 'ROLLING' }}</strong>
        <span>{{ phase === 'pending' ? '即将揭晓' : '头像滚动中' }}</span>
      </div>
    </div>

    <!-- 确认后居中揭晓 -->
    <div v-else class="raf__reveal">
      <div class="raf__winner">
        <div class="raf__winner-label">恭喜中奖</div>
        <div class="raf__winner-avatar">
          <img v-if="winner.avatar" :src="winner.avatar" alt="" />
          <span v-else>{{ (winner.nick || winner.uid || '?').slice(0, 1) }}</span>
        </div>
        <div class="raf__winner-nick">{{ winner.nick || winner.uid }}</div>
        <div v-if="prizeName" class="raf__winner-prize">{{ prizeName }}</div>
      </div>
    </div>

    <div class="raf__foot">已入场 {{ onlineCount || 0 }} 人 · 参与抽奖 {{ guests.length }} 人</div>
  </div>
</template>

<script>
const SLOT_COUNT = 26
const FALLBACK_NICKS = ['嘉宾', '好运', '幸运星', '神秘嘉宾']

export default {
  name: 'RollingAvatarFx',
  props: {
    guests: { type: Array, default: () => [] },
    draft: { type: Object, default: null },
    h5: { type: Object, default: () => ({}) },
    onlineCount: { type: Number, default: 0 },
    label: { type: String, default: '头像滚动抽奖' }
  },
  computed: {
    phase() {
      const status = (this.draft && this.draft.status) || ''
      if (status === 'confirmed') return 'reveal'
      if (status === 'pending') return 'pending'
      return 'idle'
    },
    confirmed() {
      return !!(this.draft && this.draft.status === 'confirmed')
    },
    winner() {
      const d = this.draft || {}
      if (d.winner) return d.winner
      if (Array.isArray(d.winners) && d.winners.length) return d.winners[0]
      return {}
    },
    prizeName() {
      return (this.draft && this.draft.prizeName) || ''
    },
    slots() {
      const list = (this.guests || []).filter(g => g && (g.avatar || g.nick || g.nickName))
      const items = []
      for (let i = 0; i < SLOT_COUNT; i++) {
        const src = list.length ? list[i % list.length] : null
        const nick = (src && (src.nick || src.nickName)) || FALLBACK_NICKS[i % FALLBACK_NICKS.length]
        const deg = Math.round((360 / SLOT_COUNT) * i)
        items.push({
          key: 'slot-' + i,
          avatar: (src && src.avatar) || '',
          initial: String(nick).slice(0, 1),
          style: {
            transform:
              'translate(-50%, -50%) rotate(' + deg + 'deg) translateY(calc(var(--raf-r) * -1))'
          }
        })
      }
      return items
    }
  }
}
</script>

<style scoped>
.raf {
  position: absolute;
  inset: 0;
  overflow: hidden;
  color: #f5f7fa;
  --raf-r: min(27vh, 300px);
}
.raf__bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 45%, #241a55 0%, #0d1026 62%, #05040f 100%);
  overflow: hidden;
}
.raf__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
}
.raf__glow--1 {
  width: 40vw;
  height: 40vw;
  left: 8%;
  top: 12%;
  background: rgba(80, 130, 255, 0.45);
  animation: rafFloat 9s ease-in-out infinite alternate;
}
.raf__glow--2 {
  width: 34vw;
  height: 34vw;
  right: 8%;
  bottom: 10%;
  background: rgba(255, 190, 80, 0.32);
  animation: rafFloat 11s ease-in-out infinite alternate-reverse;
}
.raf__halo {
  position: absolute;
  left: 50%;
  top: 52%;
  width: min(66vh, 720px);
  height: min(66vh, 720px);
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 1px solid rgba(255, 210, 120, 0.22);
  box-shadow: inset 0 0 min(8vw, 120px) rgba(90, 120, 255, 0.18);
}
.raf__halo.is-hot {
  border-color: rgba(255, 220, 130, 0.55);
  box-shadow: inset 0 0 min(8vw, 120px) rgba(255, 190, 70, 0.28), 0 0 min(5vw, 80px) rgba(255, 190, 70, 0.3);
  animation: rafHalo 1.2s ease-in-out infinite alternate;
}
.raf__head {
  position: relative;
  z-index: 20;
  padding-top: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.raf__banner {
  display: inline-block;
  padding: 8px 36px;
  border-radius: 24px;
  background: linear-gradient(90deg, #7b3cff, #c84bff);
  font-size: 28px;
  letter-spacing: 4px;
  box-shadow: 0 0 24px rgba(160, 80, 255, 0.55);
}
.raf__prize-name {
  display: inline-block;
  padding: 8px 30px;
  border-radius: 999px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #ffe6a0;
  background: rgba(12, 8, 26, 0.62);
  border: 1px solid rgba(255, 214, 102, 0.55);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.45), 0 0 18px rgba(255, 200, 70, 0.25);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.9);
}
.raf__ring {
  position: absolute;
  left: 50%;
  top: 53%;
  width: min(76vh, 820px);
  height: min(76vh, 820px);
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  --raf-spin: 18s;
}
.raf__wheel {
  position: absolute;
  inset: 0;
  animation: rafSpin var(--raf-spin) linear infinite;
}
.raf__ring.is-fast {
  --raf-spin: 2.4s;
}
.raf__slot {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(8.4vh, 84px);
  height: min(8.4vh, 84px);
  transform-origin: 0 0;
}
.raf__avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.28);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}
.raf__avatar.is-text {
  font-size: clamp(18px, 2.8vh, 30px);
  font-weight: 700;
  color: #ffe9b0;
  background: linear-gradient(135deg, #34558f, #1b2a4a);
}
.raf__core {
  position: relative;
  z-index: 2;
  width: min(30vh, 300px);
  height: min(30vh, 300px);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 2px solid rgba(255, 199, 88, 0.45);
  background: radial-gradient(circle, rgba(255, 199, 88, 0.16) 0%, rgba(11, 16, 32, 0.92) 70%);
  box-shadow: 0 0 80px rgba(255, 186, 66, 0.25) inset;
}
.raf__core.is-hot {
  border-color: rgba(255, 220, 130, 0.8);
  box-shadow: 0 0 60px rgba(255, 190, 70, 0.45), 0 0 80px rgba(255, 186, 66, 0.3) inset;
  animation: rafHalo 1s ease-in-out infinite alternate;
}
.raf__core strong {
  font-size: clamp(26px, 4.6vh, 48px);
  letter-spacing: 5px;
  color: #ffe9b0;
  text-shadow: 0 0 24px rgba(255, 196, 77, 0.45);
}
.raf__core span {
  margin-top: 10px;
  font-size: clamp(14px, 1.8vh, 18px);
  letter-spacing: 4px;
  opacity: 0.75;
}
.raf__reveal {
  position: absolute;
  left: 50%;
  top: 54%;
  transform: translate(-50%, -50%);
  z-index: 30;
  animation: rafPop 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
}
.raf__winner {
  width: 380px;
  padding: 18px 22px 22px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, rgba(120, 55, 200, 0.95), rgba(32, 12, 80, 0.95));
  border: 3px solid #ffd76a;
  box-shadow: 0 0 60px rgba(255, 200, 90, 0.55), 0 20px 60px rgba(0, 0, 0, 0.45);
}
.raf__winner-label {
  margin: 0 0 10px;
  font-size: 24px;
  line-height: 1.2;
  color: #ffd76a;
  letter-spacing: 4px;
}
.raf__winner-avatar {
  width: min(230px, 26vh);
  height: min(230px, 26vh);
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 72px;
  font-weight: 800;
  color: #ffe9b0;
  border: 3px solid rgba(255, 255, 255, 0.9);
  background: linear-gradient(135deg, #6d3bd6, #2a1158);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}
.raf__winner-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.raf__winner-nick {
  margin-top: 12px;
  font-size: 38px;
  line-height: 1.25;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 0 18px rgba(255, 220, 120, 0.6);
}
.raf__winner-prize {
  margin-top: 6px;
  font-size: 20px;
  line-height: 1.3;
  color: #ffe6a5;
}
.raf__foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 18px;
  z-index: 20;
  text-align: center;
  font-size: 16px;
  color: #ede8ff;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.95), 0 0 24px rgba(0, 0, 0, 0.8);
}
@keyframes rafSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes rafFloat {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(3%, 4%) scale(1.12); }
}
@keyframes rafHalo {
  from { opacity: 0.75; }
  to { opacity: 1; }
}
@keyframes rafPop {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.62); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
</style>
